import React, { useState } from 'react';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import { Container, Grid, Paper, TextField, Theme } from '@mui/material';
import UserAdminServiceInstance from '../../services/UserAdminService';
import { Link } from 'react-router-dom';
import { IOrganizer } from '../../models/IOrganizer';
import { useTranslation } from 'react-i18next';
import OntimeBreadcrumb from '../common/OntimeBreadcrumb';
import { ensureProtocol } from '../../utils/urlUtils';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            width: '100%',
            backgroundColor: theme.palette.background.paper,
        },
        inline: {
            display: 'inline',
        },
        content: {
            flexGrow: 1,
            height: '100vh',
            overflow: 'auto',
        },
        container: {
            paddingTop: theme.spacing(4),
            paddingBottom: theme.spacing(4),
        },
        paper: {
            padding: theme.spacing(2),
            display: 'flex',
            overflow: 'auto',
            flexDirection: 'column'
        },
        appBarSpacer: theme.mixins.toolbar,
    }));

interface IOrganizerCardProps {
    organizer: IOrganizer;
}

function OrganizerCard(props: IOrganizerCardProps) {
    const classes = useStyles();
    const { t } = useTranslation("common");

    const { organizer } = props;
    const organizerAvatar = organizer.picture || organizer.portraitBlobAddressUri || undefined;
    return (
        <ListItem alignItems="flex-start">
            <ListItemAvatar>
                <Link to={`/organizers/${organizer.userId}`} >
                    <Avatar alt={organizer.name} src={organizerAvatar} />
                </Link>
            </ListItemAvatar>
            <ListItemText
                primary={organizer.name}
                secondaryTypographyProps={{ component: 'div' }}
                secondary={
                    <React.Fragment>
                        <Typography
                            component="span"
                            variant="body2"
                            className={classes.inline}
                            color="textPrimary"
                        >
                            {t("organizers.lastSeasonEvents")}: {organizer.eventsLastSeason} - {t("organizers.allEvents")}: {organizer.eventsInTotal}
                        </Typography>
                        <Typography
                            component="span"
                            variant="body2"
                            className={classes.inline}
                            color="textSecondary"
                        >
                            {organizer.comment}
                        </Typography>

                        {organizer.webPage && (
                            <Typography
                                component="div"
                                variant="body2"
                                style={{ marginTop: 4 }}
                            >
                                <a href={ensureProtocol(organizer.webPage)} rel="noreferrer noopener" target="_blank">
                                    {organizer.webPage}
                                </a>
                            </Typography>
                        )}
                    </React.Fragment>
                }
            />
        </ListItem>);
}



function Organizers() {
    const [organizers, setOrganizers] = useState<IOrganizer[]>([]);
    const [filterValue, setFilterValue] = useState('');
    const classes = useStyles();
    const { t } = useTranslation("common");

    useState(() => {
        UserAdminServiceInstance.getOrganizers().then((organizers: IOrganizer[]) => {
            setOrganizers(organizers);
        });
    });

    const filteredOrganizers = filterValue.length > 0
        ? organizers.filter(o => (o.name ?? '').toLowerCase().includes(filterValue.toLowerCase()))
        : organizers;

    return (
        <main className={classes.content}>
            <div className={classes.appBarSpacer} />
            <Container maxWidth="lg" className={classes.container}>
                <Grid container spacing={3}>
                    {/* Breadcrumb */}
                    <Grid item xs={12}>
                        <OntimeBreadcrumb nameMap={{}} current={t('mainList.organizers')} />
                    </Grid>
                    <Grid item xs={12}>
                        <Paper className={classes.paper}>
                            <TextField
                                label={t('organizers.filter')}
                                variant="outlined"
                                size="small"
                                value={filterValue}
                                onChange={(e) => setFilterValue(e.target.value)}
                            />
                            <List className={classes.root} >
                                {
                                    filteredOrganizers.map(function (organizerData) {
                                        return <OrganizerCard key={organizerData.name} organizer={organizerData} />
                                    })
                                }
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
            </Container>
        </main>
    );
}

export default Organizers;